/**
 * Application Entry Point - Aegis Foundation Kit
 *
 * This file serves as the main entry point for the Aegis web application.
 * It initializes all core services, sets up the React application root,
 * and bootstraps the entire application ecosystem.
 *
 * @fileoverview Main application bootstrap and initialization
 * @description
 * ## Initialization Sequence
 *
 * 1. **Error Tracking**: Initialize Sentry for error monitoring and reporting
 * 2. **Security**: Set up security features and protections
 * 3. **Performance**: Configure performance monitoring and analytics
 * 4. **React App**: Create and render the main App component
 *
 * ## Core Services Initialized
 *
 * ### Sentry (Error Tracking)
 * - Captures and reports JavaScript errors
 * - Performance monitoring and tracing
 * - User feedback collection
 * - Release tracking and source maps
 *
 * ### Security Features
 * - Content Security Policy (CSP)
 * - XSS protection
 * - Secure headers configuration
 * - Authentication state management
 *
 * ### Performance Monitoring
 * - Core Web Vitals tracking
 * - Bundle size monitoring
 * - Runtime performance metrics
 * - User experience analytics
 *
 * @example
 * ```bash
 * # Development
 * npm run dev
 *
 * # Production build
 * npm run build
 * npm run preview
 * ```
 *
 * @since 1.0.0
 * @author Aegis Development Team
 * @license MIT
 */

import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { initSentry } from "./lib/sentry";
import { initSecurity } from "./lib/security";
import { initPerformanceMonitoring } from "./lib/performance";

// Initialize error tracking
initSentry();

// Initialize security features
initSecurity();

// Initialize performance monitoring
initPerformanceMonitoring();

// Global error handlers to capture runtime errors and unhandled promise rejections
// This helps capture stack traces (e.g. RegisterClientLocalizationsError) during dev.
window.addEventListener("error", (event) => {
	console.error("GlobalErrorEvent:", event.error ?? event.message, event);
});
window.addEventListener("unhandledrejection", (event) => {
	console.error("UnhandledRejection:", event.reason, event);
});

createRoot(document.getElementById("root")!).render(<App />);
